import { WordMatItem, WordMatState } from '@/common/types/wordMat';
import MicroAppNav from '@/common/ui/MicroAppNav';
import WordMatCtaModal from '@/common/ui/modals/WordMatCtaModal';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import Card from '@/dashboard/WordMat/Card';
import DroppableContainer from '@/dashboard/WordMat/DroppableContainer';
import {
  CustomPaletteItem,
  MatItem,
  PaletteItem,
} from '@/dashboard/WordMat/Item';
import { default as SortableContainer } from '@/dashboard/WordMat/SortableContainer';
import WordMatSettingsDialog from '@/dashboard/WordMat/WordMatSettingsDialog';
import {
  WORD_MAT_COLORS,
  getCategoryBgColor,
  getDefaultItemColor,
  getItemColorFromBgClass,
} from '@/dashboard/WordMat/colors';
import graphemes from '@/dashboard/WordMat/graphemes.json';
import spanishGraphemes from '@/dashboard/WordMat/spanishGraphemes.json';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  ArrowCounterClockwiseIcon,
  ArrowRightIcon,
  CaretDownIcon,
  CaretUpIcon,
  GearIcon,
  MinusIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Label } from '@shadcn/components/label';
import { Separator } from '@shadcn/components/separator';
import { Slider } from '@shadcn/components/slider';
import { Switch } from '@shadcn/components/switch';
import { useIsMobile } from '@shadcn/hooks/use-mobile';
import { cn } from '@shadcn/lib/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type Items = Record<UniqueIdentifier, WordMatItem[]>;

const INITIAL_ITEMS: Items = {
  '0': [],
  '1': [],
  '2': [],
};

export interface WordMatInitialData {
  paletteMode?: 'default' | 'custom';
  language?: 'en' | 'es' | 'fr';
  visibleCategories?: Record<string, boolean>;
  customCategories?: Record<string, WordMatItem[]>;
  customCategoryColors?: Record<string, string>;
  // Settings
  tileSize?: number;
  interactionMode?: 'mat' | 'stack';
  showColors?: boolean;
  alphabetColorCode?: boolean;
  tileSwapEnabled?: boolean;
}

interface WordMatProps {
  standalone?: boolean;
  presentMode?: boolean;
  initialData?: WordMatInitialData;
  onStateChange?: (state: WordMatState) => void;
}

export default function WordMat({
  standalone,
  presentMode,
  initialData,
  onStateChange,
}: WordMatProps) {
  const isMobile = useIsMobile();

  const itemIdCounter = useRef(0);

  const [initialized, setInitialised] = useState(false);
  const [items, setItems] = useState<Items>(INITIAL_ITEMS);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeSource, setActiveSource] = useState<'palette' | 'mat' | null>(
    null,
  );
  const [showMenu, setShowMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [alphabetSortMode, setAlphabetSortMode] = useState<
    'alphabetical' | 'vowels-consonants'
  >('alphabetical');
  const [language, setLanguage] = useState<'en' | 'es' | 'fr'>('en');
  const [tileSize, setTileSize] = useState(1);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showCtaModal, setShowCtaModal] = useState(false);
  // Settings state
  const [showColors, setShowColors] = useState(initialData?.showColors ?? true);
  const [alphabetColorCode, setAlphabetColorCode] = useState(
    initialData?.alphabetColorCode ?? false,
  );
  const [tileSwapEnabled, setTileSwapEnabled] = useState(
    initialData?.tileSwapEnabled ?? false,
  );
  const [interactionMode, setInteractionMode] = useState<'mat' | 'stack'>(
    initialData?.interactionMode ?? 'mat',
  );
  const [stackTargetId, setStackTargetId] = useState<UniqueIdentifier | null>(
    null,
  );
  const [visibleCategories, setVisibleCategories] = useState<{
    alphabet: boolean;
    [key: string]: boolean;
  }>({ alphabet: true, ...initialData?.visibleCategories });

  // Palette mode: 'default' shows built-in grapheme categories, 'custom' shows user categories
  const [paletteMode, setPaletteMode] = useState<'default' | 'custom'>(
    initialData?.paletteMode || 'default',
  );
  const [customCategories, setCustomCategories] = useState<
    Record<string, WordMatItem[]>
  >(initialData?.customCategories || { 'My Category': [] });
  const [customCategoryColors, setCustomCategoryColors] = useState<
    Record<string, string>
  >(initialData?.customCategoryColors || {});
  const customCategoryIdCounter = useRef(1);
  const customItemIdCounter = useRef(0);

  const graphemePalette = useMemo(() => getGraphemeItems(language), [language]);
  const alphabetPalette = useMemo(() => {
    const items = getAlphabetItems(language, alphabetColorCode);
    if (alphabetSortMode === 'alphabetical') {
      return items;
    }
    const vowels =
      language === 'fr'
        ? ['a', 'e', 'i', 'o', 'u', 'y']
        : ['a', 'e', 'i', 'o', 'u'];
    const vowelItems: WordMatItem[] = [];
    const consonantItems: WordMatItem[] = [];

    items.forEach((item) => {
      const letter = item.text;
      if (vowels.includes(letter)) {
        vowelItems.push(item);
      } else {
        consonantItems.push(item);
      }
    });

    return [...vowelItems, ...consonantItems];
  }, [alphabetSortMode, language, alphabetColorCode]);

  const visibleCategoriesCount = useMemo(
    () =>
      Object.values(visibleCategories)
        .flat()
        .filter((open) => open).length,
    [visibleCategories],
  );

  const allCategories = useMemo(() => {
    return ['alphabet', ...Object.keys(graphemePalette)];
  }, [graphemePalette]);

  useEffect(() => {
    setVisibleCategories((prev) => {
      const updated = { ...prev };
      allCategories.forEach((cat) => {
        if (!(cat in updated)) {
          updated[cat] = true;
        }
      });
      return updated;
    });
  }, [allCategories]);

  // Notify parent of state changes for saving
  useEffect(() => {
    if (!initialized) {
      setInitialised(true);
      return;
    }
    onStateChange?.({
      paletteMode,
      language,
      visibleCategories,
      customCategories,
      customCategoryColors,
      tileSize,
      interactionMode,
      showColors,
      alphabetColorCode,
      tileSwapEnabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paletteMode,
    language,
    visibleCategories,
    customCategories,
    customCategoryColors,
    tileSize,
    interactionMode,
    showColors,
    alphabetColorCode,
    tileSwapEnabled,
    onStateChange,
  ]);

  const containers = Object.keys(items);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const activeItem = useMemo(() => {
    const allItems = [
      ...Object.values(items).flat(),
      ...Object.values(graphemePalette).flat(),
      ...alphabetPalette,
      ...Object.values(customCategories).flat(),
    ];
    return allItems.find((item) => item.id === activeId);
  }, [activeId, items, graphemePalette, alphabetPalette, customCategories]);

  const findContainer = (id: UniqueIdentifier) => {
    if (id in items) return id;
    return Object.keys(items).find((key) =>
      items[key].some((item) => item.id === id),
    );
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);

    const data = active.data.current as
      | { source?: 'palette' | 'mat'; item?: WordMatItem }
      | undefined;

    setActiveSource(data?.source ?? null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveSource(null);
    setStackTargetId(null);
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    const overId = over?.id;

    const stripPreviewPrefix = (id: UniqueIdentifier) => {
      const str = String(id);
      return str.startsWith('preview-') ? str.slice('preview-'.length) : str;
    };

    if (!overId || active.id === stripPreviewPrefix(overId)) return;

    const activeData = active.data.current as
      | { source?: 'palette' | 'mat'; item?: WordMatItem }
      | undefined;
    if (!activeData || !activeData.source || !activeData.item) return;

    const overContainerId = findContainer(overId);
    if (!overContainerId) return;

    // Get the over item data to check if we're over another mat item
    const overData = over?.data.current as
      | { source?: 'palette' | 'mat'; item?: WordMatItem }
      | undefined;
    const isOverMatItem = overData?.source === 'mat';

    // Word Stack mode: don't rearrange during drag, but track target for visual feedback
    if (interactionMode === 'stack') {
      // Set stack target for visual feedback when hovering over a mat item
      if (isOverMatItem && overData?.item) {
        setStackTargetId(overId);
      } else {
        setStackTargetId(null);
      }
      return;
    }

    setItems((prev) => {
      const next = { ...prev };

      // temp id used only for this drag when sourced from palette
      const previewId =
        activeData.source === 'palette' ? `preview-${active.id}` : active.id;

      // Tile swap mode: if enabled and dragging mat item over another mat item
      if (
        tileSwapEnabled &&
        activeData.source === 'mat' &&
        isOverMatItem &&
        overData?.item
      ) {
        const fromContainer = findContainer(active.id);
        if (fromContainer) {
          const fromItems = [...next[fromContainer]];
          const fromIndex = fromItems.findIndex((i) => i.id === active.id);
          const toItems =
            fromContainer === overContainerId
              ? fromItems
              : [...next[overContainerId]];
          const toIndex = toItems.findIndex((i) => i.id === overId);

          if (fromIndex !== -1 && toIndex !== -1) {
            // Swap the items
            if (fromContainer === overContainerId) {
              // Same container swap
              const temp = fromItems[fromIndex];
              fromItems[fromIndex] = fromItems[toIndex];
              fromItems[toIndex] = temp;
              next[fromContainer] = fromItems;
            } else {
              // Cross-container swap
              const activeItem = fromItems[fromIndex];
              const overItem = toItems[toIndex];
              fromItems[fromIndex] = overItem;
              toItems[toIndex] = activeItem;
              next[fromContainer] = fromItems;
              next[overContainerId] = toItems;
            }
            return next;
          }
        }
      }

      // Default mat mode behavior (original logic)
      // 1. If dragging from a mat, remove from its original container
      if (activeData.source === 'mat') {
        const fromContainer = findContainer(active.id);
        if (fromContainer) {
          const fromItems = [...next[fromContainer]];
          const fromIndex = fromItems.findIndex((i) => i.id === active.id);
          if (fromIndex !== -1) {
            fromItems.splice(fromIndex, 1);
            next[fromContainer] = fromItems;
          }
        }
      }

      // 2. If dragging from the palette, remove any existing preview copies
      if (activeData.source === 'palette') {
        for (const containerId of Object.keys(next)) {
          const items = next[containerId];
          const filtered = items.filter((i) => i.id !== previewId);
          if (filtered.length !== items.length) {
            next[containerId as keyof typeof next] = filtered;
          }
        }
      }

      // 3. Insert into the target container at the right index
      const containerItems = [...next[overContainerId]];
      const overIndexInContainer = containerItems.findIndex(
        (i) => i.id === overId,
      );

      let targetIndex: number;
      if (overIndexInContainer === -1) {
        // over the container itself (empty area)
        targetIndex = containerItems.length;
      } else {
        targetIndex = overIndexInContainer;
      }

      // If it's already in this container (mat move or preview move), remove then re-insert for proper reordering
      const existingIndex = containerItems.findIndex((i) => i.id === previewId);
      if (existingIndex !== -1) {
        containerItems.splice(existingIndex, 1);
        if (existingIndex < targetIndex) {
          targetIndex -= 1;
        }
      }

      // Use preview item when from palette, real item when from mat
      const itemToInsert =
        activeData.source === 'palette'
          ? { ...activeData.item!, id: previewId }
          : activeData.item!;

      containerItems.splice(targetIndex, 0, itemToInsert);
      next[overContainerId] = containerItems;

      return next;
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) {
      // Remove any preview items
      setItems((prev) => {
        const next = { ...prev };
        for (const containerId of Object.keys(next)) {
          next[containerId] = next[containerId].filter(
            (item) => !String(item.id).startsWith('preview-'),
          );
        }
        return next;
      });
      setActiveId(null);
      setStackTargetId(null);
      return;
    }

    const activeData = active.data.current as
      | { source?: 'palette' | 'mat'; item?: WordMatItem }
      | undefined;
    if (!activeData || !activeData.source || !activeData.item) {
      setActiveId(null);
      setStackTargetId(null);
      return;
    }

    // Remove preview if dropped on palette (returning to palette)
    if (over.id === 'palette') {
      setItems((prev) => {
        const next = { ...prev };

        let idToRemove =
          activeData.source === 'palette' ? `preview-${active.id}` : active.id;
        for (const containerId of Object.keys(next)) {
          next[containerId] = next[containerId].filter(
            (item) => item.id !== idToRemove,
          );
        }

        return next;
      });

      setActiveId(null);
      setStackTargetId(null);
      return;
    }

    const overContainer = findContainer(over.id);
    if (!overContainer) {
      setActiveId(null);
      setStackTargetId(null);
      return;
    }

    const overData = over.data.current as
      | { source?: 'palette' | 'mat'; item?: WordMatItem }
      | undefined;
    const isOverMatItem = overData?.source === 'mat';

    setItems((prev) => {
      const next = { ...prev };

      // Handle Word Stack mode
      if (interactionMode === 'stack') {
        if (activeData.source === 'mat') {
          // Find and remove from original container
          const fromContainer = findContainer(active.id);
          if (fromContainer) {
            const fromItems = [...next[fromContainer]];
            const fromIndex = fromItems.findIndex((i) => i.id === active.id);
            if (fromIndex !== -1) {
              const [movedItem] = fromItems.splice(fromIndex, 1);
              next[fromContainer] = fromItems;

              // If dropping on another mat tile, replace it
              if (isOverMatItem && overData?.item) {
                const toItems = [...next[overContainer]];
                const targetIndex = toItems.findIndex((i) => i.id === over.id);
                if (targetIndex !== -1) {
                  // Replace the target item with the moved item
                  toItems[targetIndex] = movedItem;
                  next[overContainer] = toItems;
                } else {
                  // Fallback: add to end
                  toItems.push(movedItem);
                  next[overContainer] = toItems;
                }
              } else {
                // Dropping on empty area, add to end
                const toItems = [...next[overContainer]];
                toItems.push(movedItem);
                next[overContainer] = toItems;
              }
            }
          }
        } else if (activeData.source === 'palette') {
          // Palette item in stack mode
          const newItem = {
            ...activeData.item!,
            id: `item-${itemIdCounter.current++}`,
          };

          if (isOverMatItem && overData?.item) {
            // Replace the target tile
            const toItems = [...next[overContainer]];
            const targetIndex = toItems.findIndex((i) => i.id === over.id);
            if (targetIndex !== -1) {
              toItems[targetIndex] = newItem;
              next[overContainer] = toItems;
            } else {
              // Fallback: add to end
              toItems.push(newItem);
              next[overContainer] = toItems;
            }
          } else {
            // Dropping on empty area, add to end
            const toItems = [...next[overContainer]];
            toItems.push(newItem);
            next[overContainer] = toItems;
          }
        }
        return next;
      }

      // Turn preview into a permanent instance (normal sort mode)
      if (activeData.source === 'palette') {
        const previewId = `preview-${active.id}`;
        const containerItems = next[overContainer];

        next[overContainer] = containerItems.map((item) =>
          item.id === previewId
            ? {
                ...item,
                id: `item-${itemIdCounter.current++}`,
              }
            : item,
        );
      }

      return next;
    });

    setActiveId(null);
    setStackTargetId(null);
  };

  const addContainer = () => {
    const newId = String(containers.length);
    setItems((prev) => ({ ...prev, [newId]: [] }));
  };

  const removeContainer = () => {
    if (containers.length <= 1) return;
    const lastKey = containers[containers.length - 1];
    setItems((prev) => {
      const newItems = { ...prev };
      delete newItems[lastKey];
      return newItems;
    });
  };

  const clearAll = () => {
    setItems(Object.fromEntries(containers.map((id) => [id, []])));
  };

  const toggleCategory = (category: string) => {
    setVisibleCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleAllCategories = () => {
    setVisibleCategories((prev) => {
      return Object.fromEntries(
        Object.entries(prev).map(([cat]) => [
          cat,
          cat === 'alphabet'
            ? true
            : visibleCategoriesCount === Object.keys(visibleCategories).length
              ? false
              : true,
        ]),
      ) as typeof prev;
    });
  };

  const handleDiacriticChange = (itemId: UniqueIdentifier, newText: string) => {
    setItems((prev) => {
      const next = { ...prev };
      for (const containerId of Object.keys(next)) {
        const containerItems = next[containerId];
        const itemIndex = containerItems.findIndex(
          (item) => item.id === itemId,
        );
        if (itemIndex !== -1) {
          const updatedItems = [...containerItems];
          updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            text: newText,
          };
          next[containerId] = updatedItems;
          break;
        }
      }
      return next;
    });
  };

  // Custom category helpers
  const addCustomCategory = () => {
    const newName = `Category ${customCategoryIdCounter.current++}`;
    setCustomCategories((prev) => ({
      ...prev,
      [newName]: [],
    }));
    trackMixpanelEvent('Word Mat Custom Category Added', {
      categoryName: newName,
    });
  };

  const removeCustomCategory = (categoryName: string) => {
    setCustomCategories((prev) => {
      const next = { ...prev };
      delete next[categoryName];
      return next;
    });
    // Also remove color
    setCustomCategoryColors((prev) => {
      const { [categoryName]: _, ...rest } = prev;
      return rest;
    });
    trackMixpanelEvent('Word Mat Custom Category Removed', {
      categoryName,
    });
  };

  const addItemsToCategory = (categoryName: string, texts: string[]) => {
    setCustomCategories((prev) => {
      const next = { ...prev };
      // Get the item color for this category if it has a color set
      const categoryBgColor = customCategoryColors[categoryName];
      const itemColor = categoryBgColor
        ? getItemColorFromBgClass(categoryBgColor)
        : null;
      const newItems = texts.map((text) => {
        const item: { id: string; text: string; color?: string } = {
          id: `custom-item-${customItemIdCounter.current++}`,
          text: text,
        };
        if (itemColor) {
          item.color = itemColor;
        }
        return item;
      });
      next[categoryName] = [...(next[categoryName] || []), ...newItems];
      return next;
    });
    trackMixpanelEvent('Word Mat Custom Items Added', {
      categoryName,
      items: texts,
      itemCount: texts.length,
    });
  };

  const removeItemFromCategory = (
    categoryName: string,
    itemId: UniqueIdentifier,
  ) => {
    setCustomCategories((prev) => {
      const next = { ...prev };
      next[categoryName] = next[categoryName].filter(
        (item) => item.id !== itemId,
      );
      return next;
    });
    trackMixpanelEvent('Word Mat Custom Item Removed', {
      categoryName,
      itemId: String(itemId),
    });
  };

  const clearCategory = (categoryName: string) => {
    setCustomCategories((prev) => ({
      ...prev,
      [categoryName]: [],
    }));
  };

  const renameCategory = (oldName: string, newName: string) => {
    if (oldName === newName || !newName.trim()) return;
    const trimmedNewName = newName.trim();
    setCustomCategories((prev) => {
      const entries = Object.entries(prev);
      const newEntries = entries.map(([key, value]) =>
        key === oldName ? [trimmedNewName, value] : [key, value],
      );
      return Object.fromEntries(newEntries);
    });
    // Also update color key if exists
    setCustomCategoryColors((prev) => {
      if (prev[oldName]) {
        const { [oldName]: color, ...rest } = prev;
        return { ...rest, [trimmedNewName]: color };
      }
      return prev;
    });
    trackMixpanelEvent('Word Mat Custom Category Renamed', {
      oldName,
      newName: trimmedNewName,
    });
  };

  const setCategoryColor = (
    categoryName: string,
    colorValue: string | null,
  ) => {
    if (colorValue === null) {
      // Remove color (set to no color)
      setCustomCategoryColors((prev) => {
        const next = { ...prev };
        delete next[categoryName];
        return next;
      });

      // Remove colors from all items in this category
      setCustomCategories((prev) => {
        const next = { ...prev };
        if (next[categoryName]) {
          next[categoryName] = next[categoryName].map((item) => {
            const { color, ...rest } = item;
            return rest;
          });
        }
        return next;
      });
      trackMixpanelEvent('Word Mat Custom Category Color Changed', {
        categoryName,
        colorValue: 'none',
      });
    } else {
      // Update category background color
      setCustomCategoryColors((prev) => ({
        ...prev,
        [categoryName]: colorValue,
      }));

      // Update all items in this category with the corresponding item color
      const itemColor = getItemColorFromBgClass(colorValue);
      setCustomCategories((prev) => {
        const next = { ...prev };
        if (next[categoryName]) {
          next[categoryName] = next[categoryName].map((item) => ({
            ...item,
            color: itemColor,
          }));
        }
        return next;
      });
      const colorName =
        WORD_MAT_COLORS.find((c) => c.bgClass === colorValue)?.name ||
        'Unknown';
      trackMixpanelEvent('Word Mat Custom Category Color Changed', {
        categoryName,
        colorValue,
        colorName,
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {standalone && !presentMode && (
        <>
          <MicroAppNav
            feedbackSource="wordmat"
            ctaContent={
              <Button
                variant="link"
                className="hidden sm:flex items-center gap-1 p-0"
                onClick={() => {
                  trackMixpanelEvent('Word Mat Save CTA Clicked', {
                    source: 'wordmat-micronav',
                  });
                  setShowCtaModal(true);
                }}
              >
                Save &amp; edit Word Mats for free
                <ArrowRightIcon className="size-4" />
              </Button>
            }
          />
          <WordMatCtaModal
            isOpen={showCtaModal}
            onClose={() => setShowCtaModal(false)}
            wordMatState={{
              paletteMode,
              language,
              visibleCategories,
              customCategories,
              customCategoryColors,
              tileSize,
              interactionMode,
              showColors,
              alphabetColorCode,
              tileSwapEnabled,
            }}
          />
        </>
      )}
      {/* Mode Toggle (standalone only) and Settings - Top Right */}
      {!presentMode && (
        <div className="flex justify-end items-center gap-4 px-4 sm:px-6 lg:px-8  pt-4">
          {standalone && (
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-sm font-medium',
                  paletteMode === 'default' ? 'text-gray-900' : 'text-gray-400',
                )}
              >
                Standard
              </span>
              <Switch
                checked={paletteMode === 'custom'}
                onCheckedChange={(checked) => {
                  trackMixpanelEvent('Word Mat Palette Mode Changed', {
                    changedTo: checked ? 'custom' : 'default',
                    changedFrom: checked ? 'default' : 'custom',
                  });
                  setPaletteMode(checked ? 'custom' : 'default');
                }}
              />
              <span
                className={cn(
                  'text-sm font-medium',
                  paletteMode === 'custom' ? 'text-gray-900' : 'text-gray-400',
                )}
              >
                Build your own{' '}
                <span className="text-xs text-gray-400 font-normal">
                  (beta)
                </span>
              </span>
            </div>
          )}
          <Button
            onClick={() => setShowSettingsDialog(true)}
            size="icon"
            variant="outline"
            className="h-8 w-8"
          >
            <GearIcon size={16} />
          </Button>
          <WordMatSettingsDialog
            open={showSettingsDialog}
            onOpenChange={setShowSettingsDialog}
            showColors={showColors}
            onShowColorsChange={setShowColors}
            alphabetColorCode={alphabetColorCode}
            onAlphabetColorCodeChange={setAlphabetColorCode}
            tileSwapEnabled={tileSwapEnabled}
            onTileSwapEnabledChange={setTileSwapEnabled}
            interactionMode={interactionMode}
            onInteractionModeChange={setInteractionMode}
          />
        </div>
      )}
      <div className="flex flex-col gap-6 mx-6 xl:mx-12 mt-4 xl:mt-8 xl:py-12 justify-center">
        <DndContext
          sensors={sensors}
          collisionDetection={(args) => {
            const pointerCollisions = pointerWithin(args);
            if (pointerCollisions.length > 0) {
              return pointerCollisions;
            }
            return closestCorners(args);
          }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="flex flex-col gap-4 xl:gap-14">
            <div className="flex items-center justify-center gap-4">
              <div className="flex p-0.5 xl:p-2 bg-gray-50 rounded-lg border-2 border-gray-400">
                {containers.map((containerId, i) => (
                  <React.Fragment key={i}>
                    <SortableContainer
                      key={containerId}
                      id={containerId}
                      items={items[containerId].map((item) => item.id)}
                      className="min-w-[54px] min-h-[54px] lg:min-w-[72px] lg:min-h-[72px] xl:min-h-[100px] xl:min-w-[100px] p-1.5 xl:p-2"
                    >
                      <div
                        className={cn(
                          'flex gap-1.5 xl:gap-2',
                          interactionMode === 'stack' && 'relative',
                        )}
                      >
                        {items[containerId].map((item) => (
                          <MatItem
                            key={item.id}
                            item={item}
                            onDiacriticChange={handleDiacriticChange}
                            language={language}
                          >
                            <Card
                              text={item.text}
                              color={showColors ? item.color : undefined}
                              active
                              sizeScale={tileSize}
                              stackTarget={item.id === stackTargetId}
                            />
                          </MatItem>
                        ))}
                      </div>
                    </SortableContainer>
                    {i < containers.length - 1 && (
                      <Separator
                        orientation="vertical"
                        className="h-[40px] lg:h-[50px] xl:h-[80px] w-[2px] my-auto bg-gray-400"
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              <ButtonGroup
                containers={containers.length}
                onAdd={addContainer}
                onRemove={removeContainer}
                onClear={clearAll}
                horizontal={!isMobile}
              />
            </div>

            <DroppableContainer
              id="palette"
              className="px-2 max-w-screen xl:max-w-[80vw] mx-auto flex flex-col gap-4"
            >
              {/* Default Palette */}
              {paletteMode === 'default' && visibleCategories.alphabet && (
                <div className="flex flex-col gap-2 min-w-0 col-span-2 md:col-span-3 lg:col-span-4">
                  {(language === 'es' || language === 'fr') && (
                    <span className="text-xs text-gray-500 whitespace-nowrap text-end">
                      Hover on vowels in word building area for diacritics
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-start flex-col gap-1 md:items-center md:flex-row md:gap-2">
                      <span className="text-sm font-medium text-gray-600">
                        Alphabet
                      </span>
                      <DropdownMenu
                        open={showSortMenu}
                        onOpenChange={setShowSortMenu}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="gap-2" size="sm">
                            {showSortMenu ? (
                              <CaretUpIcon size={16} />
                            ) : (
                              <CaretDownIcon size={16} />
                            )}
                            {alphabetSortMode === 'alphabetical'
                              ? 'Alphabetical'
                              : 'Vowels/Consonants'}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              trackMixpanelEvent(
                                'Word Mat Alphabet Sort Mode Changed',
                                { changedTo: 'alphabetical' },
                              );
                              setAlphabetSortMode('alphabetical');
                            }}
                          >
                            Alphabetical
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              trackMixpanelEvent(
                                'Word Mat Alphabet Sort Mode Changed',
                                { changedTo: 'vowels-consonants' },
                              );
                              setAlphabetSortMode('vowels-consonants');
                            }}
                          >
                            Vowels/Consonants
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex items-end md:items-center flex-col gap-1 md:flex-row md:gap-4">
                      <TileSizeControl
                        value={tileSize}
                        onChange={setTileSize}
                      />
                      <InteractionModeToggle
                        value={interactionMode}
                        onChange={setInteractionMode}
                      />
                      <div className="flex flex-col items-end md:items-center gap-1">
                        <LanguageSwitcher
                          language={language}
                          onLanguageChange={setLanguage}
                        />
                      </div>
                      <DropdownMenu open={showMenu} onOpenChange={setShowMenu}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            onClick={() => setShowMenu(!showMenu)}
                            variant="outline"
                            className="gap-2"
                            size="sm"
                          >
                            {showMenu ? (
                              <CaretUpIcon size={16} />
                            ) : (
                              <CaretDownIcon size={16} />
                            )}
                            Categories
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side={isMobile ? 'bottom' : 'right'}
                        >
                          <DropdownMenuLabel>
                            Visible Categories
                          </DropdownMenuLabel>
                          <div className="flex flex-col gap-2">
                            <div className="grid grid-cols-2 gap-2 p-2">
                              {allCategories.map((category) => (
                                <Label
                                  key={category}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <Checkbox
                                    checked={
                                      visibleCategories[category] ?? true
                                    }
                                    onCheckedChange={() => {
                                      trackMixpanelEvent(
                                        'Word Mat Category Toggled',
                                        {
                                          category,
                                          changedTo: visibleCategories[category]
                                            ? 'off'
                                            : 'on',
                                        },
                                      );
                                      toggleCategory(category);
                                    }}
                                    disabled={category === 'alphabet'}
                                  />
                                  <span className="text-sm text-gray-700 capitalize">
                                    {category}
                                  </span>
                                </Label>
                              ))}
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => {
                                trackMixpanelEvent(
                                  'Word Mat All Categories Toggled',
                                  {
                                    changedTo:
                                      visibleCategoriesCount ===
                                      Object.keys(visibleCategories).length
                                        ? 'all hidden'
                                        : 'all visible',
                                  },
                                );
                                toggleAllCategories();
                              }}
                            >
                              {visibleCategoriesCount ===
                              Object.keys(visibleCategories).length
                                ? 'Hide All'
                                : 'Show All'}
                            </Button>
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div
                    className={cn(
                      'rounded-lg p-2',
                      showColors
                        ? `${getCategoryBgColor(
                            'alphabet',
                            graphemePalette,
                          )} border border-gray-200/50`
                        : 'bg-white border border-gray-300',
                    )}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {alphabetPalette.map((item) => (
                        <PaletteItem key={item.id} item={item}>
                          <Card
                            text={item.text}
                            color={showColors ? item.color : undefined}
                            sizeScale={tileSize}
                          />
                        </PaletteItem>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {paletteMode === 'default' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3 auto-rows-min">
                  {Object.keys(graphemePalette)
                    .filter((category) => visibleCategories[category] ?? true)
                    .map((category) => (
                      <div
                        key={category}
                        className="flex flex-col gap-1 min-w-0"
                      >
                        <span className="text-sm font-medium text-gray-600">
                          {category}
                        </span>
                        <div
                          className={cn(
                            'rounded-lg p-2',
                            showColors
                              ? `${getCategoryBgColor(
                                  category,
                                  graphemePalette,
                                )} border border-gray-200/50`
                              : 'bg-white border border-gray-300',
                          )}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {graphemePalette[category].map((item) => (
                              <PaletteItem key={item.id} item={item}>
                                <Card
                                  text={item.text}
                                  color={showColors ? item.color : undefined}
                                  sizeScale={tileSize}
                                />
                              </PaletteItem>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Custom Palette */}
              {paletteMode === 'custom' && (
                <div className="flex flex-col gap-4">
                  <span className="text-xs text-gray-500 whitespace-nowrap text-end">
                    Custom word mat is in beta, provide feedback above.
                  </span>
                  {/* Controls for custom mode */}
                  <div className="flex justify-between items-center gap-4">
                    <Button
                      variant="outline"
                      onClick={addCustomCategory}
                      className="w-fit"
                    >
                      <PlusIcon size={16} className="mr-2" />
                      Add Category
                    </Button>
                    <div className="flex items-center gap-4">
                      <TileSizeControl
                        value={tileSize}
                        onChange={setTileSize}
                      />
                      <InteractionModeToggle
                        value={interactionMode}
                        onChange={setInteractionMode}
                      />
                    </div>
                  </div>

                  {/* Custom categories */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4 auto-rows-min">
                    {Object.entries(customCategories).map(
                      ([categoryName, categoryItems]) => (
                        <CustomCategoryCard
                          key={categoryName}
                          categoryName={categoryName}
                          items={categoryItems}
                          tileSize={tileSize}
                          categoryColor={customCategoryColors[categoryName]}
                          categoryColors={WORD_MAT_COLORS}
                          onAddItems={(texts) =>
                            addItemsToCategory(categoryName, texts)
                          }
                          onRemoveItem={(itemId) =>
                            removeItemFromCategory(categoryName, itemId)
                          }
                          onClear={() => clearCategory(categoryName)}
                          onRemoveCategory={() =>
                            removeCustomCategory(categoryName)
                          }
                          onRenameCategory={(newName) =>
                            renameCategory(categoryName, newName)
                          }
                          onSetCategoryColor={(colorValue) =>
                            setCategoryColor(categoryName, colorValue)
                          }
                          showColors={showColors}
                          canRemove={Object.keys(customCategories).length > 1}
                        />
                      ),
                    )}
                  </div>
                </div>
              )}
            </DroppableContainer>
          </div>

          <DragOverlay
            dropAnimation={activeSource === 'palette' ? null : undefined}
          >
            {activeItem && (
              <Card
                text={activeItem.text}
                color={showColors ? activeItem.color : undefined}
                active
                sizeScale={tileSize}
              />
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}

function ButtonGroup({
  containers,
  onAdd,
  onRemove,
  onClear,
  horizontal,
}: {
  containers: number;
  onAdd: () => void;
  onRemove: () => void;
  onClear: () => void;
  horizontal?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1',
        horizontal && 'flex-row-reverse',
      )}
    >
      <Button
        onClick={() => {
          trackMixpanelEvent('Word Mat Cleared');
          onClear();
        }}
        size="icon"
        className="h-8 w-8"
      >
        <ArrowCounterClockwiseIcon size={16} />
      </Button>
      <Button
        onClick={() => {
          trackMixpanelEvent('Word Mat Box Added');
          onAdd();
        }}
        size="icon"
        variant="secondary"
        disabled={containers >= 12}
        className="h-8 w-8"
      >
        <PlusIcon size={16} />
      </Button>
      <Button
        onClick={() => {
          trackMixpanelEvent('Word Mat Box Removed');
          onRemove();
        }}
        size="icon"
        variant="secondary"
        disabled={containers <= 1}
        className="h-8 w-8"
      >
        <MinusIcon size={16} />
      </Button>
    </div>
  );
}

function LanguageSwitcher({
  language,
  onLanguageChange,
}: {
  language: 'en' | 'es' | 'fr';
  onLanguageChange: (lang: 'en' | 'es' | 'fr') => void;
}) {
  const [open, setOpen] = useState(false);

  const getLanguageLabel = (lang: 'en' | 'es' | 'fr') => {
    switch (lang) {
      case 'en':
        return 'English';
      case 'es':
        return 'Español';
      case 'fr':
        return 'Français';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {open ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}
          {getLanguageLabel(language)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onClick={() => {
            trackMixpanelEvent('Word Mat English Selected');
            onLanguageChange('en');
          }}
        >
          English
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            trackMixpanelEvent('Word Mat Spanish Selected');
            onLanguageChange('es');
          }}
        >
          Español
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            trackMixpanelEvent('Word Mat French Selected');
            onLanguageChange('fr');
          }}
        >
          Français
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TileSizeControl({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  const handleSliderChange = (values: number[]) => {
    onChange(values[0]);
  };

  const handleReset = () => {
    onChange(1);
  };

  return (
    <div className="flex items-center gap-2">
      <Label className="text-sm font-medium text-gray-600 whitespace-nowrap">
        Tile Size
      </Label>
      <div className="flex items-center gap-2">
        <Slider
          value={[value]}
          onValueChange={handleSliderChange}
          min={1}
          max={2}
          step={0.1}
          className={cn('w-20 bg-gray-100 rounded-md')}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          className="h-7 w-7 p-0"
          title="Reset Tile Size"
        >
          <ArrowCounterClockwiseIcon size={14} />
        </Button>
      </div>
    </div>
  );
}

function InteractionModeToggle({
  value,
  onChange,
}: {
  value: 'mat' | 'stack';
  onChange: (value: 'mat' | 'stack') => void;
}) {
  const handleModeChange = (newMode: 'mat' | 'stack') => {
    if (newMode !== value) {
      trackMixpanelEvent('Word Mat Interaction Mode Changed', {
        changedFrom: value,
        changedTo: newMode,
      });
      onChange(newMode);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex rounded-md border border-gray-300 overflow-hidden">
        <button
          onClick={() => handleModeChange('mat')}
          className={cn(
            'px-3 py-1 text-sm font-medium transition-colors',
            value === 'mat'
              ? 'bg-primary text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50',
          )}
        >
          Sort
        </button>
        <button
          onClick={() => handleModeChange('stack')}
          className={cn(
            'px-3 py-1 text-sm font-medium transition-colors',
            value === 'stack'
              ? 'bg-primary text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50',
          )}
        >
          Stack
        </button>
      </div>
    </div>
  );
}

function CustomCategoryCard({
  categoryName,
  items,
  tileSize,
  categoryColor,
  categoryColors,
  onAddItems,
  onRemoveItem,
  onClear,
  onRemoveCategory,
  onRenameCategory,
  onSetCategoryColor,
  canRemove,
  showColors,
}: {
  categoryName: string;
  items: WordMatItem[];
  tileSize: number;
  categoryColor?: string;
  categoryColors: Array<{
    name: string;
    bgClass: string;
    borderClass: string;
    bgRgba: string;
  }>;
  onAddItems: (texts: string[]) => void;
  onRemoveItem: (itemId: UniqueIdentifier) => void;
  onClear: () => void;
  onRemoveCategory: () => void;
  onRenameCategory: (newName: string) => void;
  onSetCategoryColor: (colorValue: string | null) => void;
  canRemove: boolean;
  showColors: boolean;
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(categoryName);

  const handleNameSubmit = () => {
    if (editedName.trim() && editedName !== categoryName) {
      onRenameCategory(editedName.trim());
    } else {
      setEditedName(categoryName);
    }
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setEditedName(categoryName);
      setIsEditingName(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 min-w-0 group/card">
      {/* Category header */}
      <div className="flex items-center justify-between gap-2">
        {isEditingName ? (
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={handleNameKeyDown}
            autoFocus
            className="text-sm font-medium text-gray-600 bg-white border border-blue-500 rounded px-1 py-0.5 focus:outline-none"
          />
        ) : (
          <span
            className="text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-900"
            onClick={() => {
              setEditedName(categoryName);
              setIsEditingName(true);
            }}
            title="Click to rename"
          >
            {categoryName}
          </span>
        )}
        <div className="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
          <DropdownMenu
            open={showColorPicker}
            onOpenChange={setShowColorPicker}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0 relative"
                title="Change color"
              >
                <div
                  className="w-4 h-4 rounded border border-gray-300"
                  style={{
                    backgroundColor:
                      categoryColors.find((c) => c.bgClass === categoryColor)
                        ?.bgRgba || undefined,
                  }}
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Category Color</DropdownMenuLabel>
              <div className="grid grid-cols-4 gap-2 p-2">
                {/* No color option */}
                <button
                  onClick={() => {
                    onSetCategoryColor(null);
                    setShowColorPicker(false);
                  }}
                  className={cn(
                    'w-8 h-8 rounded border-2 border-gray-300 transition-all hover:scale-110 relative bg-white',
                    !categoryColor ? 'ring-2 ring-offset-1 ring-gray-400' : '',
                  )}
                  title="No color"
                ></button>
                {categoryColors.map((color) => (
                  <button
                    key={color.bgClass}
                    onClick={() => {
                      onSetCategoryColor(color.bgClass);
                      setShowColorPicker(false);
                    }}
                    className={cn(
                      'w-8 h-8 rounded border-2 transition-all hover:scale-110',
                      color.borderClass,
                      categoryColor === color.bgClass
                        ? 'ring-2 ring-offset-1 ring-gray-400'
                        : '',
                    )}
                    style={{ backgroundColor: color.bgRgba }}
                    title={color.name}
                  />
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="h-7 px-2 text-xs"
          >
            <PlusIcon size={12} className="mr-1" />
            Add Items
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={items.length === 0}
            className="h-7 w-7 p-0"
            title="Reset category"
          >
            <ArrowCounterClockwiseIcon size={14} />
          </Button>
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveCategory}
              className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
              title="Remove category"
            >
              <XIcon size={14} />
            </Button>
          )}
        </div>
      </div>

      {/* Category content */}
      <div
        className={cn(
          'rounded-lg p-2 border flex-1 min-h-[60px]',
          showColors && categoryColor
            ? `${categoryColor} ${
                categoryColors.find((c) => c.bgClass === categoryColor)
                  ?.borderClass || 'border-gray-200'
              }`
            : 'bg-gray-50 border-gray-200',
        )}
      >
        {items.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-2">No items yet</p>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            {items.map((item) => (
              <CustomPaletteItem
                key={item.id}
                item={item}
                onRemove={onRemoveItem}
              >
                <Card
                  text={item.text}
                  color={showColors ? item.color : undefined}
                  sizeScale={tileSize}
                />
              </CustomPaletteItem>
            ))}
          </div>
        )}
      </div>

      {/* Add items dialog */}
      <AddItemsDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        categoryName={categoryName}
        onAddItems={onAddItems}
      />
    </div>
  );
}

function AddItemsDialog({
  open,
  onOpenChange,
  categoryName,
  onAddItems,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryName: string;
  onAddItems: (texts: string[]) => void;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleSave = () => {
    if (!inputValue.trim()) return;

    // Split by comma and clean up each item
    const texts = inputValue
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    if (texts.length > 0) {
      onAddItems(texts);
      setInputValue('');
      onOpenChange(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Items to {categoryName}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">
              Enter items separated by commas (e.g., mat, sat, cat)
            </label>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="mat, sat, cat, bat..."
              rows={3}
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setInputValue('');
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!inputValue.trim()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function getAlphabetItems(
  language: 'en' | 'es' | 'fr' = 'en',
  colorCode: boolean = false,
): WordMatItem[] {
  const ENGLISH_ALPHABET = 'abcdefghijklmnopqrstuvwxyz'.split('');
  const SPANISH_ALPHABET = 'abcdefghijklmnñopqrstuvwxyz'.split('');
  const FRENCH_ALPHABET = 'abcçdefghijklmnopqrstuvwxyz'.split('');

  const alphabet =
    language === 'es'
      ? SPANISH_ALPHABET
      : language === 'fr'
        ? FRENCH_ALPHABET
        : ENGLISH_ALPHABET;

  const vowels =
    language === 'fr'
      ? ['a', 'e', 'i', 'o', 'u', 'y']
      : ['a', 'e', 'i', 'o', 'u'];
  const vowelColor = 'rgba(251, 113, 133, 1)';
  const consonantColor = 'rgba(14, 165, 233, 1)';

  return alphabet.map((letter, i) => {
    const text = letter;
    const isVowel = vowels.includes(letter.toLowerCase());

    return {
      id: `${text}-${i}`,
      text,
      color: colorCode ? (isVowel ? vowelColor : consonantColor) : undefined,
    };
  });
}

export function getGraphemeItems(
  language: 'en' | 'es' | 'fr' = 'en',
): Record<string, WordMatItem[]> {
  if (language === 'fr') {
    return {};
  }

  const selectedGraphemes = language === 'es' ? spanishGraphemes : graphemes;

  const entries = Object.entries(selectedGraphemes).map(
    ([category, graphemes], categoryIdx) => [
      category,
      graphemes.map((g, itemIdx) => {
        const text = g;
        return {
          id: `${category}-${text}-${itemIdx}}`,
          text,
          color: getDefaultItemColor(category, categoryIdx),
        };
      }),
    ],
  );

  return Object.fromEntries(entries);
}
